import { useState, useCallback, useEffect } from 'react';
import { Card, Btn, Input, Select, Field } from '../../components/UI';
import { useToast } from '../../components/Toast';
import { bagtapyanAPI, personelAPI } from '../../utils/api';
import { exportCSV, printTable } from '../../utils/exportHelper';

const StatusBadge = ({ status }) => {
  const colors = {
    SELESAI: { bg: '#DCFCE7', color: '#166534' },
    GAGAL: { bg: '#FEE2E2', color: '#991B1B' },
    MENUNGGU: { bg: '#FEF3C7', color: '#92400E' },
    DIPROSES: { bg: '#DBEAFE', color: '#1E40AF' },
    DRAFT: { bg: '#F3F4F6', color: '#374151' },
    FINAL: { bg: '#E0E7FF', color: '#3730A3' },
    TERCETAK: { bg: '#DBEAFE', color: '#1E40AF' },
    PERSIAPAN: { bg: '#FEF3C7', color: '#92400E' },
    VALIDASI: { bg: '#DBEAFE', color: '#1E40AF' },
  };
  const c = colors[status] || { bg: '#F3F4F6', color: '#374151' };
  return <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: c.bg, color: c.color }}>{status}</span>;
};

const thStyle = { padding: '10px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748B', borderBottom: '2px solid #E2E8F0' };
const tdStyle = { padding: '10px 14px', fontSize: 13, borderBottom: '1px solid #F1F5F9' };
const rowStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 16 };

const Pager = ({ page, total, limit, onChange }) => {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  if (totalPages <= 1) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, justifyContent: 'center' }}>
      <Btn onClick={() => onChange(page - 1)} disabled={page <= 1} variant="secondary">← Prev</Btn>
      <span style={{ fontSize: 12, color: '#6B7280', padding: '0 8px' }}>Hal {page} / {totalPages}</span>
      <Btn onClick={() => onChange(page + 1)} disabled={page >= totalPages} variant="secondary">Next →</Btn>
    </div>
  );
};

// ── UPLOAD DATA TAB ───────────────────────────────────────────────────────
const UploadTab = ({ kotamaList }) => {
  const [uploads, setUploads] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ tipe_upload: '', jenis_pendidikan: '', kotama_id: '', file: null });
  const { toast } = useToast();

  const loadUploads = useCallback(async () => {
    setLoading(true);
    try {
      const r = await bagtapyanAPI.getUpload(`?page=${page}&limit=10`);
      if (r.success) { setUploads(r.data); setTotal(r.pagination.total); }
    } catch (err) { toast(err.message, 'error'); }
    finally { setLoading(false); }
  }, [page, toast]);

  useEffect(() => { loadUploads(); }, [loadUploads]);

  const handleUpload = async () => {
    if (!form.tipe_upload || !form.file) { toast('Tipe upload dan file harus diisi', 'error'); return; }
    try {
      const r = await bagtapyanAPI.uploadFile(form);
      if (r.success) { toast('File berhasil diunggah'); setForm({ tipe_upload: '', jenis_pendidikan: '', kotama_id: '', file: null }); loadUploads(); }
    } catch (err) { toast(err.message, 'error'); }
  };

  return (
    <>
      <Card>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Upload Data Baru</h3>
        <div style={rowStyle}>
          <Field label="Tipe Upload"><Select value={form.tipe_upload} onChange={(e) => setForm({ ...form, tipe_upload: e.target.value })}
            options={[{ value: '', label: '-- Pilih Tipe --' }, { value: 'dikma', label: 'Data Awal (Dikma)' }, { value: 'kecabangan', label: 'Data Kecabangan' }, { value: 'diktuk', label: 'Data Diktuk' }, { value: 'psdp', label: 'Data PSDP' }]} /></Field>
          <Field label="Jenis Pendidikan"><Select value={form.jenis_pendidikan} onChange={(e) => setForm({ ...form, jenis_pendidikan: e.target.value })}
            options={[{ value: '', label: '-- Pilih --' }, { value: 'MILITER', label: 'Militer' }, { value: 'REGULER', label: 'Reguler' }, { value: 'KHUSUS', label: 'Khusus' }]} /></Field>
        </div>
        <div style={rowStyle}>
          <Field label="Kotama"><Select value={form.kotama_id} onChange={(e) => setForm({ ...form, kotama_id: e.target.value })}
            options={[{ value: '', label: '-- Pilih Kotama --' }, ...kotamaList.map(k => ({ value: k.id, label: k.nama }))]} /></Field>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>File</label>
            <input type="file" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] })} style={{ padding: '6px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13 }} />
          </div>
        </div>
        <Btn onClick={handleUpload} disabled={loading}>{loading ? 'Mengunggah...' : 'Upload'}</Btn>
      </Card>
      <Card>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Daftar Upload</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#F8FAFC' }}>
              <th style={thStyle}>Tipe</th><th style={thStyle}>File</th><th style={thStyle}>Jumlah</th><th style={thStyle}>Status</th><th style={thStyle}>Waktu Upload</th>
            </tr></thead>
            <tbody>
              {uploads.map(u => (
                <tr key={u.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{u.tipe_upload}</td>
                  <td style={tdStyle}>{u.file_name}</td>
                  <td style={tdStyle}>{u.jumlah_data} baris</td>
                  <td style={tdStyle}><StatusBadge status={u.status} /></td>
                  <td style={{ ...tdStyle, fontSize: 12, color: '#64748B' }}>{new Date(u.uploaded_at).toLocaleString('id')}</td>
                </tr>
              ))}
              {uploads.length === 0 && <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: '#9CA3AF' }}>Belum ada data</td></tr>}
            </tbody>
          </table>
        </div>
        <Pager page={page} total={total} limit={10} onChange={setPage} />
      </Card>
    </>
  );
};

// ── NRP GENERATION TAB ────────────────────────────────────────────────────
const NRPTab = () => {
  const [nrpData, setNrpData] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ jenis_pengolahan: '', pangkat: '' });
  const { toast } = useToast();

  const loadNRP = useCallback(async () => {
    setLoading(true);
    try {
      const r = await bagtapyanAPI.getNRP(`?page=${page}&limit=10`);
      if (r.success) { setNrpData(r.data); setTotal(r.pagination.total); }
    } catch (err) { toast(err.message, 'error'); }
    finally { setLoading(false); }
  }, [page, toast]);

  useEffect(() => { loadNRP(); }, [loadNRP]);

  const handleGenerate = async () => {
    if (!form.jenis_pengolahan || !form.pangkat) { toast('Jenis pengolahan dan pangkat harus dipilih', 'error'); return; }
    try {
      const r = await bagtapyanAPI.generateNRP(form);
      if (r.success) { toast(r.message || 'NRP berhasil dibuat'); loadNRP(); }
    } catch (err) { toast(err.message, 'error'); }
  };

  const pangkatOptions = ['Jenderal', 'Letnan Jenderal', 'Mayor Jenderal', 'Brigadir Jenderal', 'Kolonel', 'Letnan Kolonel', 'Mayor', 'Kapten', 'Letnan Satu', 'Letnan Dua', 'Sersan Mayor', 'Sersan Kepala', 'Sersan', 'Kopral Kepala', 'Kopral Satu', 'Kopral', 'Prajurit Kepala', 'Prajurit Satu', 'Prajurit Dua'];

  return (
    <>
      <Card>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Penetapan NRP</h3>
        <div style={rowStyle}>
          <Field label="Jenis Pengolahan"><Select value={form.jenis_pengolahan} onChange={(e) => setForm({ ...form, jenis_pengolahan: e.target.value })}
            options={[{ value: '', label: '-- Pilih --' }, { value: 'dikma', label: 'Dikma' }, { value: 'psdp', label: 'PSDP' }]} /></Field>
          <Field label="Pangkat"><Select value={form.pangkat} onChange={(e) => setForm({ ...form, pangkat: e.target.value })}
            options={[{ value: '', label: '-- Pilih Pangkat --' }, ...pangkatOptions.map(p => ({ value: p, label: p }))]} /></Field>
        </div>
        <Btn onClick={handleGenerate} disabled={loading}>{loading ? 'Membuat...' : 'Generate NRP'}</Btn>
      </Card>
      <Card>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Daftar NRP</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#F8FAFC' }}>
              <th style={thStyle}>Jenis Pengolahan</th><th style={thStyle}>Pangkat</th><th style={thStyle}>NRP Awal</th><th style={thStyle}>NRP Akhir</th><th style={thStyle}>Jumlah</th><th style={thStyle}>Status</th>
            </tr></thead>
            <tbody>
              {nrpData.map(n => (
                <tr key={n.id}>
                  <td style={tdStyle}>{n.jenis_pengolahan}</td><td style={tdStyle}>{n.pangkat}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{n.nrp_awal}</td><td style={{ ...tdStyle, fontFamily: 'monospace' }}>{n.nrp_akhir}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{n.jumlah}</td><td style={tdStyle}><StatusBadge status={n.status} /></td>
                </tr>
              ))}
              {nrpData.length === 0 && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9CA3AF' }}>Belum ada data</td></tr>}
            </tbody>
          </table>
        </div>
        <Pager page={page} total={total} limit={10} onChange={setPage} />
      </Card>
    </>
  );
};

// ── SALARY ASSIGNMENT TAB ─────────────────────────────────────────────────
const GajiTab = () => {
  const [gajiData, setGajiData] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchNrp, setSearchNrp] = useState('');

  const loadGaji = useCallback(async () => {
    setLoading(true);
    try {
      const qs = `?page=${page}&limit=20${searchNrp ? `&nrp=${searchNrp}` : ''}`;
      const r = await bagtapyanAPI.getGaji(qs);
      if (r.success) { setGajiData(r.data); setTotal(r.pagination.total); }
    } catch {}
    finally { setLoading(false); }
  }, [page, searchNrp]);

  useEffect(() => { loadGaji(); }, [loadGaji]);

  return (
    <>
      <Card>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Cari Gaji Pokok</h3>
        <Input placeholder="Cari NRP..." value={searchNrp} onChange={(e) => { setSearchNrp(e.target.value); setPage(1); }} />
      </Card>
      <Card>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Daftar Gaji Pokok</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#F8FAFC' }}>
              <th style={thStyle}>NRP</th><th style={thStyle}>Nama</th><th style={thStyle}>Pangkat</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Gaji Dikma</th><th style={{ ...thStyle, textAlign: 'right' }}>Gaji Diktub</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Gaji Dikmil</th><th style={thStyle}>TMT</th>
            </tr></thead>
            <tbody>
              {gajiData.map(g => (
                <tr key={g.id}>
                  <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{g.nrp}</td><td style={tdStyle}>{g.nama}</td><td style={tdStyle}>{g.pangkat}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{g.gaji_pokok_dikma?.toLocaleString('id')}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{g.gaji_pokok_diktub?.toLocaleString('id')}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{g.gaji_pokok_dikmil?.toLocaleString('id')}</td>
                  <td style={{ ...tdStyle, fontSize: 12 }}>{g.tmt_gaji}</td>
                </tr>
              ))}
              {gajiData.length === 0 && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#9CA3AF' }}>Belum ada data</td></tr>}
            </tbody>
          </table>
        </div>
        <Pager page={page} total={total} limit={20} onChange={setPage} />
      </Card>
    </>
  );
};

// ── DECISIONS TAB ─────────────────────────────────────────────────────────
const KeputusanTab = () => {
  const [keputusans, setKeputusans] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ jenis_keputusan: '', jenis_pengolahan: '', no_kep: '', tgl_kep: '' });
  const { toast } = useToast();

  const loadKeputusan = useCallback(async () => {
    setLoading(true);
    try {
      const r = await bagtapyanAPI.getKeputusan(`?page=${page}&limit=10`);
      if (r.success) { setKeputusans(r.data); setTotal(r.pagination.total); }
    } catch (err) { toast(err.message, 'error'); }
    finally { setLoading(false); }
  }, [page, toast]);

  useEffect(() => { loadKeputusan(); }, [loadKeputusan]);

  const handleCreate = async () => {
    if (!form.jenis_keputusan || !form.no_kep) { toast('Jenis keputusan dan nomor harus diisi', 'error'); return; }
    try {
      const r = await bagtapyanAPI.createKeputusan(form);
      if (r.success) { toast('Keputusan berhasil dibuat'); setForm({ jenis_keputusan: '', jenis_pengolahan: '', no_kep: '', tgl_kep: '' }); loadKeputusan(); }
    } catch (err) { toast(err.message, 'error'); }
  };

  return (
    <>
      <Card>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Buat Keputusan</h3>
        <div style={rowStyle}>
          <Field label="Jenis Keputusan"><Select value={form.jenis_keputusan} onChange={(e) => setForm({ ...form, jenis_keputusan: e.target.value })}
            options={[{ value: '', label: '-- Pilih --' }, { value: 'asli', label: 'Keputusan Asli' }, { value: 'salinan', label: 'Keputusan Salinan' }, { value: 'petikan', label: 'Keputusan Petikan' }]} /></Field>
          <Field label="Jenis Pengolahan"><Select value={form.jenis_pengolahan} onChange={(e) => setForm({ ...form, jenis_pengolahan: e.target.value })}
            options={[{ value: '', label: '-- Pilih --' }, { value: 'dikma', label: 'Dikma' }, { value: 'diktuk', label: 'Diktuk' }, { value: 'psdp', label: 'PSDP' }]} /></Field>
        </div>
        <div style={rowStyle}>
          <Field label="Nomor Keputusan"><Input value={form.no_kep} onChange={(e) => setForm({ ...form, no_kep: e.target.value })} placeholder="KEP-2026-001" /></Field>
          <Field label="Tanggal Keputusan"><Input type="date" value={form.tgl_kep} onChange={(e) => setForm({ ...form, tgl_kep: e.target.value })} /></Field>
        </div>
        <Btn onClick={handleCreate} disabled={loading}>{loading ? 'Membuat...' : 'Buat Keputusan'}</Btn>
      </Card>
      <Card>
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin:0 }}>Daftar Keputusan</h3>
          <div style={{ display:'flex',gap:6 }}>
            <Btn size="sm" variant="ghost" onClick={() => { const h=['Nomor','Jenis','Pengolahan','Tanggal','Status']; exportCSV('btpn_keputusan', h, keputusans.map(k=>[k.no_kep,k.jenis_keputusan,k.jenis_pengolahan,k.tgl_kep,k.status])); }}>CSV</Btn>
            <Btn size="sm" variant="ghost" onClick={() => { const h=['Nomor','Jenis','Pengolahan','Tanggal','Status']; printTable('Keputusan Bagtapyan', h, keputusans.map(k=>[k.no_kep,k.jenis_keputusan,k.jenis_pengolahan,k.tgl_kep,k.status])); }}>Print</Btn>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#F8FAFC' }}>
              <th style={thStyle}>Nomor</th><th style={thStyle}>Jenis</th><th style={thStyle}>Pengolahan</th><th style={thStyle}>Tanggal</th><th style={thStyle}>Status</th><th style={thStyle}>Aksi</th>
            </tr></thead>
            <tbody>
              {keputusans.map(k => (
                <tr key={k.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{k.no_kep}</td><td style={{ ...tdStyle, textTransform: 'capitalize' }}>{k.jenis_keputusan}</td>
                  <td style={tdStyle}>{k.jenis_pengolahan}</td><td style={{ ...tdStyle, fontSize: 12 }}>{k.tgl_kep}</td><td style={tdStyle}><StatusBadge status={k.status} /></td>
                  <td style={tdStyle}>
                    {k.status === 'DRAFT' && <Btn size="sm" variant="danger" onClick={async () => {
                      if (!window.confirm('Hapus keputusan ini?')) return;
                      try { await bagtapyanAPI.deleteKeputusan(k.id); toast('Keputusan dihapus'); loadKeputusan(); } catch(e) { toast(e.message, 'error'); }
                    }}>Hapus</Btn>}
                  </td>
                </tr>
              ))}
              {keputusans.length === 0 && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9CA3AF' }}>Belum ada data</td></tr>}
            </tbody>
          </table>
        </div>
        <Pager page={page} total={total} limit={10} onChange={setPage} />
      </Card>
    </>
  );
};

// ── DISTRIBUTION TAB ──────────────────────────────────────────────────────
const DistribusiTab = () => {
  const [distribusi, setDistribusi] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const loadDistribusi = useCallback(async () => {
    setLoading(true);
    try {
      const r = await bagtapyanAPI.getDistribusi(`?page=${page}&limit=10`);
      if (r.success) { setDistribusi(r.data); setTotal(r.pagination.total); }
    } catch (err) { toast(err.message, 'error'); }
    finally { setLoading(false); }
  }, [page, toast]);

  useEffect(() => { loadDistribusi(); }, [loadDistribusi]);

  return (
    <Card>
      <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Daftar Distribusi Data</h3>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: '#F8FAFC' }}>
            <th style={thStyle}>Jenis Pengolahan</th><th style={thStyle}>NRP Awal</th><th style={thStyle}>NRP Akhir</th><th style={thStyle}>Jumlah</th><th style={thStyle}>Tanggal</th><th style={thStyle}>Status</th><th style={thStyle}>Aksi</th>
          </tr></thead>
          <tbody>
            {distribusi.map(d => (
              <tr key={d.id}>
                <td style={tdStyle}>{d.jenis_pengolahan}</td><td style={{ ...tdStyle, fontFamily: 'monospace' }}>{d.nrp_awal}</td>
                <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{d.nrp_akhir}</td><td style={{ ...tdStyle, fontWeight: 600 }}>{d.jumlah}</td>
                <td style={{ ...tdStyle, fontSize: 12 }}>{d.tgl_distribusi}</td><td style={tdStyle}><StatusBadge status={d.status} /></td>
                <td style={tdStyle}>
                  {d.status === 'PERSIAPAN' && <div style={{ display:'flex', gap:4 }}>
                    <Btn size="sm" onClick={async () => {
                      try { await bagtapyanAPI.validasiDistribusi(d.id); toast('Distribusi divalidasi'); loadDistribusi(); } catch(e) { toast(e.message, 'error'); }
                    }}>Validasi</Btn>
                    <Btn size="sm" variant="danger" onClick={async () => {
                      if (!window.confirm('Hapus distribusi ini?')) return;
                      try { await bagtapyanAPI.deleteDistribusi(d.id); toast('Distribusi dihapus'); loadDistribusi(); } catch(e) { toast(e.message, 'error'); }
                    }}>Hapus</Btn>
                  </div>}
                  {d.status === 'VALIDASI' && <Btn size="sm" variant="success" onClick={async () => {
                    try { await bagtapyanAPI.distribusikanData(d.id); toast('Data berhasil didistribusikan'); loadDistribusi(); } catch(e) { toast(e.message, 'error'); }
                  }}>Distribusikan</Btn>}
                </td>
              </tr>
            ))}
            {distribusi.length === 0 && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#9CA3AF' }}>Belum ada data</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={page} total={total} limit={10} onChange={setPage} />
    </Card>
  );
};

// ── HISTORY TAB ───────────────────────────────────────────────────────────
const RiwayatTab = () => {
  const [riwayat, setRiwayat] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [jenisPengolahan, setJenisPengolahan] = useState('');

  const loadRiwayat = useCallback(async () => {
    setLoading(true);
    try {
      const qs = `?page=${page}&limit=20${jenisPengolahan ? `&jenis_pengolahan=${jenisPengolahan}` : ''}`;
      const r = await bagtapyanAPI.getRiwayat(qs);
      if (r.success) { setRiwayat(r.data); setTotal(r.pagination.total); }
    } catch {}
    finally { setLoading(false); }
  }, [page, jenisPengolahan]);

  useEffect(() => { loadRiwayat(); }, [loadRiwayat]);

  return (
    <>
      <Card>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Filter Riwayat</h3>
        <div style={{ maxWidth: 280 }}>
          <Select value={jenisPengolahan} onChange={(e) => { setJenisPengolahan(e.target.value); setPage(1); }}
            options={[{ value: '', label: 'Semua Jenis Pengolahan' }, { value: 'dikma', label: 'Dikma' }, { value: 'diktuk', label: 'Diktuk' }, { value: 'psdp', label: 'PSDP' }]} />
        </div>
      </Card>
      <Card>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Daftar Riwayat</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#F8FAFC' }}>
              <th style={thStyle}>Jenis Pengolahan</th><th style={thStyle}>Tahap</th><th style={thStyle}>NRP</th><th style={thStyle}>Status</th><th style={thStyle}>Waktu</th>
            </tr></thead>
            <tbody>
              {riwayat.map(r => (
                <tr key={r.id}>
                  <td style={tdStyle}>{r.jenis_pengolahan}</td><td style={tdStyle}>{r.tahap_proses}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{r.nrp}</td><td style={tdStyle}><StatusBadge status={r.status} /></td>
                  <td style={{ ...tdStyle, fontSize: 12, color: '#64748B' }}>{new Date(r.created_at).toLocaleString('id')}</td>
                </tr>
              ))}
              {riwayat.length === 0 && <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: '#9CA3AF' }}>Belum ada data</td></tr>}
            </tbody>
          </table>
        </div>
        <Pager page={page} total={total} limit={20} onChange={setPage} />
      </Card>
    </>
  );
};

// ── MAIN COMPONENT ────────────────────────────────────────────────────────
const BagtapyanPage = () => {
  const [activeTab, setActiveTab] = useState('upload');
  const [kotamaList, setKotamaList] = useState([]);

  useEffect(() => {
    personelAPI.getKotama().then(r => { if (r.data) setKotamaList(r.data); }).catch(() => {});
  }, []);

  const tabs = [
    { id: 'upload', label: 'Upload Data' },
    { id: 'nrp', label: 'Penetapan NRP' },
    { id: 'gaji', label: 'Penetapan Gaji' },
    { id: 'keputusan', label: 'Keputusan' },
    { id: 'distribusi', label: 'Distribusi' },
    { id: 'riwayat', label: 'Riwayat' },
  ];

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0F172A', margin: 0 }}>BAGTAPYAN</h1>
        <p style={{ color: '#64748B', fontSize: 13, margin: '4px 0 0' }}>Bagian Penetapan (Appointment Department)</p>
      </div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #E2E8F0' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              color: activeTab === t.id ? '#2563EB' : '#64748B',
              borderBottom: activeTab === t.id ? '2px solid #2563EB' : '2px solid transparent', marginBottom: -2 }}>
            {t.label}
          </button>
        ))}
      </div>
      {activeTab === 'upload' && <UploadTab kotamaList={kotamaList} />}
      {activeTab === 'nrp' && <NRPTab />}
      {activeTab === 'gaji' && <GajiTab />}
      {activeTab === 'keputusan' && <KeputusanTab />}
      {activeTab === 'distribusi' && <DistribusiTab />}
      {activeTab === 'riwayat' && <RiwayatTab />}
    </div>
  );
};

export default BagtapyanPage;
